import { useState, useCallback } from "react";
import * as Location from "expo-location";
import { Platform, Alert } from "react-native";

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  heading?: number | null;
  speed?: number | null;
}

interface UseLocationReturn {
  location: LocationData | null;
  loading: boolean;
  error: string | null;
  getCurrentLocation: () => Promise<LocationData | null>;
  requestPermissions: () => Promise<boolean>;
}

const useLocation = (): UseLocationReturn => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestPermissions = useCallback(async (): Promise<boolean> => {
    try {
      if (Platform.OS === "web") {
        // Web geolocation API
        return new Promise((resolve) => {
          if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser");
            // Alert.alert(
            //   "Location Access Recommended",
            //   "Your location helps keep your open house details accurate and tied to the right property. You can enable location access in your browser settings if you'd like to use this feature.",
            //   [{ text: "OK" }]
            // );
            resolve(false);
            return;
          }

          navigator.geolocation.getCurrentPosition(
            () => resolve(true),
            () => {
              setError("Location permission denied");
              // Alert.alert(
              //   "Location Access Recommended",
              //   "Your location helps keep your open house details accurate and tied to the right property. You can enable location access in your browser settings if you'd like to use this feature.",
              //   [{ text: "OK" }]
              // );
              resolve(false);
            }
          );
        });
      }

      const { status: existingStatus } = await Location.getForegroundPermissionsAsync();
      
      if (existingStatus === "granted") {
        return true;
      }

      const { status } = await Location.requestForegroundPermissionsAsync();
      
      if (status !== "granted") {
        setError("Location permission denied");
        // Alert.alert(
        //   "Location Access Recommended",
        //   "Your location helps keep your open house details accurate and tied to the right property. You can enable location access in Settings > Privacy > Location Services > Open House by Brivity, and choose 'While Using the App'.",
        //   [{ text: "OK" }]
        // );
        return false;
      }

      return true;
    } catch (err: any) {
      setError(err?.message || "Failed to request location permissions");
      return false;
    }
  }, []);

  const getCurrentLocation = useCallback(async (): Promise<LocationData | null> => {
    setLoading(true);
    setError(null);

    try {
      if (Platform.OS === "web") {
        // For web, check permission first and show alert if denied
        const hasPermission = await requestPermissions();
        
        if (!hasPermission) {
          setLoading(false);
          // Alert already shown in requestPermissions, but ensure we return null
          return null;
        }

        // If permission granted, get location
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const locationData: LocationData = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                heading: position.coords.heading,
                speed: position.coords.speed,
              };
              setLocation(locationData);
              setLoading(false);
              resolve(locationData);
            },
            (err) => {
              const errorMessage = err.message || "Failed to get location";
              setError(errorMessage);
              setLoading(false);
              // Show alert for web location denial (in case it wasn't shown in requestPermissions)
              if (err.code === 1) {
                // PERMISSION_DENIED
                // Alert.alert(
                //   "Location Access Recommended",
                //   "Your location helps keep your open house details accurate and tied to the right property. You can enable location access in your browser settings if you'd like to use this feature.",
                //   [{ text: "OK" }]
                // );
              }
              resolve(null);
            },
            {
              enableHighAccuracy: true,
              timeout: 15000,
              maximumAge: 10000,
            }
          );
        });
      }

      // For native platforms
      const hasPermission = await requestPermissions();
      
      if (!hasPermission) {
        setLoading(false);
        return null;
      }

      // Get current position for native platforms
      const currentLocation = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });

      const locationData: LocationData = {
        latitude: currentLocation.coords.latitude,
        longitude: currentLocation.coords.longitude,
        accuracy: currentLocation.coords.accuracy,
        altitude: currentLocation.coords.altitude,
        heading: currentLocation.coords.heading,
        speed: currentLocation.coords.speed,
      };

      setLocation(locationData);
      setLoading(false);
      return locationData;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to get current location";
      setError(errorMessage);
      setLoading(false);
      Alert.alert("Location Error", errorMessage, [{ text: "OK" }]);
      return null;
    }
  }, [requestPermissions]);

  return {
    location,
    loading,
    error,
    getCurrentLocation,
    requestPermissions,
  };
};

export default useLocation;

